import React from "react";
import { Helmet } from "react-helmet";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

const sections = [
  {
    title: "Information We Collect",
    body: [
      "Account details such as your name, username, email address, password status, and sign-in provider.",
      "Recipe, dashboard, ingredient, and nutrition label data you create or upload in CaloriesPal.",
      "Food search interactions, device/browser information, and basic technical logs used to keep the service secure and reliable.",
      "Messages you send through support, contact, password reset, verification, or account deletion forms.",
    ],
  },
  {
    title: "How We Use Information",
    body: [
      "To provide account access, recipe dashboards, nutrition calculations, food search, exports, and support tools.",
      "To send service emails such as email verification, password reset links, account notices, and support responses.",
      "To improve performance, security, fraud prevention, troubleshooting, and product quality.",
      "To comply with legal obligations and enforce service policies when necessary.",
    ],
  },
  {
    title: "Food and Nutrition Data",
    body: [
      "Public food database information may come from external public datasets such as USDA FoodData Central.",
      "Nutrition values are provided for planning and labeling support and should be reviewed by qualified professionals when used for regulated business purposes.",
      "Your private recipes and dashboards remain tied to your account unless you export, share, or request deletion of that account data.",
    ],
  },
  {
    title: "Sharing and Disclosure",
    body: [
      "We do not sell your personal account data.",
      "We may share limited data with service providers that help operate hosting, email delivery, analytics, authentication, security, and support workflows.",
      "We may disclose information if required by law, to protect users, or to defend the security and integrity of CaloriesPal.",
    ],
  },
  {
    title: "Data Retention",
    body: [
      "We keep account data while your account is active or as needed to provide the service.",
      "Support, security, and technical records may be retained for a reasonable period to resolve issues and protect the platform.",
      "You can request account deletion at any time from the Delete Account Request page.",
    ],
  },
  {
    title: "Your Choices",
    body: [
      "You can update your profile and password from your account settings.",
      "You can request a password reset from the sign-in page if you lose access.",
      "You can request deletion of your account and related user data using the dedicated request form.",
    ],
  },
  {
    title: "Security",
    body: [
      "We use administrative, technical, and operational safeguards designed to protect account access and service data.",
      "No internet service can guarantee absolute security, so users should keep passwords private and use secure email accounts.",
    ],
  },
  {
    title: "Contact",
    body: [
      "For privacy questions, account access issues, or deletion requests, contact us at contact@caloriespal.com.",
    ],
  },
];

const PrivacyPolicy = () => (
  <>
    <Helmet>
      <title>Privacy Policy | CaloriesPal</title>
      <meta
        name="description"
        content="Read the CaloriesPal Privacy Policy, including what account, recipe, nutrition, and support data we collect and how users can request account deletion."
      />
      <link rel="canonical" href="https://caloriespal.com/privacy-policy" />
      <meta property="og:title" content="Privacy Policy | CaloriesPal" />
      <meta
        property="og:description"
        content="How CaloriesPal handles account, recipe, food search, and support data."
      />
      <meta property="og:url" content="https://caloriespal.com/privacy-policy" />
    </Helmet>

    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", py: { xs: 4, md: 8 } }}>
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Button
            component={RouterLink}
            to="/"
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
          >
            Back to home
          </Button>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 6 },
              borderRadius: 4,
              border: "1px solid #e2e8f0",
              boxShadow: "0 24px 70px rgba(15,23,42,0.08)",
            }}
          >
            <Typography variant="overline" color="success.main" fontWeight={800}>
              CaloriesPal Legal
            </Typography>
            <Typography variant="h3" component="h1" fontWeight={900} sx={{ mt: 1 }}>
              Privacy Policy
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              Effective date: June 30, 2026
            </Typography>
            <Typography sx={{ mt: 3, fontSize: 18, lineHeight: 1.8 }}>
              This Privacy Policy explains how CaloriesPal collects, uses, stores,
              and protects information when you use our website, food search tools,
              recipe dashboards, nutrition label features, and account services.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Stack spacing={4}>
              {sections.map((section) => (
                <Box key={section.title}>
                  <Typography variant="h5" component="h2" fontWeight={800} gutterBottom>
                    {section.title}
                  </Typography>
                  <Stack component="ul" spacing={1.2} sx={{ pl: 3, m: 0 }}>
                    {section.body.map((item) => (
                      <Typography component="li" color="text.secondary" key={item}>
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>

            <Paper
              elevation={0}
              sx={{
                mt: 5,
                p: 3,
                borderRadius: 3,
                bgcolor: "#ecfccb",
                border: "1px solid #bef264",
              }}
            >
              <Typography variant="h6" fontWeight={800}>
                Need to delete your account?
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                Submit a request and our team will verify ownership before removing
                account data.
              </Typography>
              <Button component={RouterLink} to="/delete-account-request" variant="contained">
                Request account deletion
              </Button>
            </Paper>
          </Paper>
        </Stack>
      </Container>
    </Box>
  </>
);

export default PrivacyPolicy;
